import { Box, Flex, Image, Text } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import React from 'react'
import { useErc20TokenContractAddressesQuery } from 'apollo/generated/graphqlClient'
import { CopyToClipboardButton } from 'components/CopyToClipboardButton'
import { InfoPopup } from 'components/InfoPopup'
import { Label } from 'components/Label'
import { Link } from 'components/Link'
import { Pagination } from 'components/Pagination'
import { Table } from 'components/Table'
import {
  DETAIL_CARD_PADDING_X,
  NOT_APPLICABLE,
  PAGE_LENGTH,
  TIMESTAMP_COLUMN_FORMAT,
  TRUNCATED_MOBILE_TEXT_WIDTH,
} from 'constants/common'
import { Routes } from 'constants/routes'
import { useGetErc20Transfers } from 'generated/reactQueryClient'
import type { Erc20TransferListItem as Erc20Item } from 'generated/reactQueryClient'
import { useCursorPagination } from 'hooks/useCursorPagination'
import { useThemeColors } from 'hooks/useThemeColors'
import { useTransactionHashTruncateOptions } from 'hooks/useTransactionHashTruncateOptions'
import { getAssetSymbol, getFormattedAsset, getTruncatedText } from 'utils/common'
import {
  getTimestampColumnFormatValue,
  getTimestampColumnTooltipFormatValue,
  getTimestampColumnTooltipText,
} from 'utils/dayjs'
import { getImageSrc } from 'utils/getImageSrc'

const Address = ({ hash }: { hash: string }) => {
  const { query } = useRouter()
  const TRANSACTION_HASH_TRUNCATE_OPTIONS = useTransactionHashTruncateOptions()
  const truncatedHash = getTruncatedText(hash, ...TRANSACTION_HASH_TRUNCATE_OPTIONS)

  if (hash === query.hash) {
    return <Text variant="text3medium">{truncatedHash}</Text>
  }

  return (
    <Link
      href={Routes.addressDetail({ hash })}
      onClick={(event) => {
        event.stopPropagation()
      }}
    >
      {truncatedHash}
    </Link>
  )
}

export const ERCToken = () => {
  const COLORS = useThemeColors()
  const { query } = useRouter()
  const { cursor, setCursor, direction, setDirection } = useCursorPagination()
  const [isAgeTimestampFormat, setIsAgeTimestampFormat] = React.useState<boolean>(true)

  const hash = query.hash as string

  const { data: { data, paginationContext } = { data: [] }, isLoading } = useGetErc20Transfers({
    limit: PAGE_LENGTH,
    address: hash,
    ...(cursor && { [direction]: cursor }),
  })

  const { data: { erc20TokenContractAddresses } = {} } = useErc20TokenContractAddressesQuery()

  const timestampFormat = isAgeTimestampFormat
    ? TIMESTAMP_COLUMN_FORMAT.AGE
    : TIMESTAMP_COLUMN_FORMAT.DATE_TIME

  return (
    <Box mx={-DETAIL_CARD_PADDING_X}>
      <Flex
        h={55}
        px={5}
        py={2}
        bgColor={COLORS.grey07}
        borderBottom={{ base: 'unset', lg: '1px solid' }}
        borderColor={{ base: 'unset', lg: COLORS.grey06 }}
      >
        <Pagination
          ml="auto"
          isDisabled={isLoading || data?.length === 0}
          hasNextPage={!!paginationContext?.next}
          hasPreviousPage={!!paginationContext?.previous}
          onNextPage={() => {
            setDirection('next')
            setCursor(paginationContext?.next ?? '')
          }}
          onPreviousPage={() => {
            setDirection('previous')
            setCursor(paginationContext?.previous ?? '')
          }}
        />
      </Flex>
      <Table
        variant="secondary"
        hasTopContent={false}
        data={data}
        isLoading={isLoading}
        withTopRadius={false}
        renderMobileRow={(row: Erc20Item) => {
          const tokenSymbol = getAssetSymbol(row.transfers?.[0]?.symbol || row.symbol)
          const erc20TokenAsset = erc20TokenContractAddresses?.find(
            ({ symbol }: { symbol: string }) => symbol === tokenSymbol
          )

          let tokenAsset = '/assets/ethereum2.svg'

          if (tokenSymbol && erc20TokenContractAddresses?.length) {
            if (erc20TokenAsset?.type) {
              tokenAsset = getImageSrc({
                contractAddressL2: erc20TokenAsset.contract_address_l2,
                type: erc20TokenAsset.type,
              })
            }
          }

          return (
            <Table.MobileRow key={row.txHash}>
              {({ isExpanded }) => (
                <Flex flex="1" flexDirection="column" textAlign="left" gap={3}>
                  <Table.MobileRowCell label="Txn Hash:">
                    <Link
                      href={Routes.transactionDetail({ txHash: row.txHash, activeTab: 0 })}
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                      maxW={TRUNCATED_MOBILE_TEXT_WIDTH}
                      isTruncated
                    >
                      {row.txHash}
                    </Link>
                  </Table.MobileRowCell>
                  <Table.AnimatedMobileRowCell
                    label={
                      data?.some((tx) => tx.timestamp) ? (
                        <Table.InteractiveMobileHeaderCell
                          variant="text3regular"
                          onClick={() => {
                            setIsAgeTimestampFormat((prev) => !prev)
                          }}
                        >
                          {timestampFormat}:
                        </Table.InteractiveMobileHeaderCell>
                      ) : (
                        `${timestampFormat}:`
                      )
                    }
                    isVisible={isExpanded}
                  >
                    <InfoPopup
                      title={
                        timestampFormat === TIMESTAMP_COLUMN_FORMAT.AGE
                          ? 'ERC20 Token Datetime:'
                          : 'ERC20 Token Age:'
                      }
                      triggerElement={
                        <Text
                          variant="text3regular"
                          color={COLORS.grey03}
                          onClick={(event) => {
                            event.stopPropagation()
                          }}
                        >
                          {row.timestamp
                            ? getTimestampColumnFormatValue(row.timestamp, timestampFormat)
                            : NOT_APPLICABLE}
                        </Text>
                      }
                    >
                      {row.timestamp
                        ? getTimestampColumnTooltipFormatValue(row.timestamp, timestampFormat)
                        : NOT_APPLICABLE}
                    </InfoPopup>
                  </Table.AnimatedMobileRowCell>
                  <Table.MobileRowCell label="From:">
                    <Address hash={row.from} />
                  </Table.MobileRowCell>
                  <Table.MobileRowCell label={undefined}>
                    <Label variant={row.from === hash ? 'critical' : 'success'}>
                      {row.from === hash ? 'Out' : 'In'}
                    </Label>
                  </Table.MobileRowCell>
                  <Table.MobileRowCell label="To:">
                    <Address hash={row.to} />
                  </Table.MobileRowCell>
                  <Table.MobileRowCell label="Value:">
                    <Flex alignItems="center">
                      <Image
                        alt="Token Asset"
                        src={tokenAsset}
                        {...(tokenSymbol && tokenAsset?.length && { boxSize: 4 })}
                      />
                      <Text
                        variant="text3regular"
                        ml={1}
                        w={TRUNCATED_MOBILE_TEXT_WIDTH}
                        isTruncated
                        whiteSpace="nowrap"
                      >
                        {getFormattedAsset({ transfers: row.transfers || [row], value: row.value })}
                      </Text>
                    </Flex>
                  </Table.MobileRowCell>
                  <Table.AnimatedMobileRowCell label="Token:" isVisible={isExpanded}>
                    <Flex alignItems="center">
                      <Image
                        alt="Token"
                        src={tokenAsset}
                        {...(tokenSymbol && tokenAsset?.length && { boxSize: 4 })}
                      />
                      <Text ml={1} variant="text3regular">
                        {row.tokenName} ({row.symbol})
                      </Text>
                    </Flex>
                  </Table.AnimatedMobileRowCell>
                </Flex>
              )}
            </Table.MobileRow>
          )
        }}
        renderTableHeader={() => (
          <>
            <Table.HeaderCell>Txn Hash</Table.HeaderCell>
            {data?.some((tx) => tx.timestamp) ? (
              <Table.InteractiveHeaderCell
                tooltipText={getTimestampColumnTooltipText(timestampFormat)}
                onClick={() => {
                  setIsAgeTimestampFormat((prev) => !prev)
                }}
              >
                {timestampFormat}
              </Table.InteractiveHeaderCell>
            ) : (
              <Table.HeaderCell>{timestampFormat}</Table.HeaderCell>
            )}
            <Table.HeaderCell>From</Table.HeaderCell>
            <Table.HeaderCell />
            <Table.HeaderCell>To</Table.HeaderCell>
            <Table.HeaderCell>Value</Table.HeaderCell>
            <Table.HeaderCell>Token</Table.HeaderCell>
          </>
        )}
        renderTableRow={(row: Erc20Item) => {
          const tokenSymbol = getAssetSymbol(row.transfers?.[0]?.symbol || row.symbol)
          const erc20TokenAsset = erc20TokenContractAddresses?.find(
            ({ symbol }: { symbol: string }) => symbol === tokenSymbol
          )

          let tokenAsset = '/assets/ethereum2.svg'

          if (tokenSymbol && erc20TokenContractAddresses?.length) {
            if (erc20TokenAsset?.type) {
              tokenAsset = getImageSrc({
                contractAddressL2: erc20TokenAsset?.contract_address_l2,
                type: erc20TokenAsset?.type,
              })
            }
          }

          return (
            <Table.Row key={row.txHash}>
              <Table.RowCell>
                <Flex alignItems="center" gap={1}>
                  <Link
                    href={Routes.transactionDetail({ txHash: row.txHash, activeTab: 0 })}
                    maxW={180}
                    isTruncated
                  >
                    {row.txHash}
                  </Link>
                  <CopyToClipboardButton text={row.txHash} />
                </Flex>
              </Table.RowCell>
              <Table.RowCell>
                <InfoPopup
                  title={
                    timestampFormat === TIMESTAMP_COLUMN_FORMAT.AGE
                      ? 'ERC20 Token Datetime:'
                      : 'ERC20 Token Age:'
                  }
                  triggerElement={
                    <Text variant="text3regular" color={COLORS.grey03}>
                      {row.timestamp
                        ? getTimestampColumnFormatValue(row.timestamp, timestampFormat)
                        : NOT_APPLICABLE}
                    </Text>
                  }
                >
                  {row.timestamp
                    ? getTimestampColumnTooltipFormatValue(row.timestamp, timestampFormat)
                    : NOT_APPLICABLE}
                </InfoPopup>
              </Table.RowCell>
              <Table.RowCell>
                <Flex alignItems="center" gap={1}>
                  <Address hash={row.from} />
                  <CopyToClipboardButton text={row.from} />
                </Flex>
              </Table.RowCell>
              <Table.RowCell>
                <Label variant={row.from === hash ? 'critical' : 'success'}>
                  {row.from === hash ? 'Out' : 'In'}
                </Label>
              </Table.RowCell>
              <Table.RowCell>
                <Flex alignItems="center" gap={1}>
                  <Address hash={row.to} />
                  <CopyToClipboardButton text={row.to} />
                </Flex>
              </Table.RowCell>
              <Table.RowCell>
                <Flex alignItems="center">
                  <Image
                    alt="Token Asset"
                    src={tokenAsset}
                    {...(tokenSymbol && erc20TokenContractAddresses?.length && { boxSize: 4 })}
                  />
                  <Text ml={1} variant="text3regular">
                    {getFormattedAsset({
                      transfers: row.transfers || [row],
                      value: row.value,
                    })}
                  </Text>
                </Flex>
              </Table.RowCell>
              <Table.RowCell>
                <Flex alignItems="center">
                  <Image
                    alt="Token"
                    src={tokenAsset}
                    {...(tokenSymbol && erc20TokenContractAddresses?.length && { boxSize: 4 })}
                  />
                  <Text ml={1} variant="text3regular">
                    {row.tokenName} ({row.symbol})
                  </Text>
                </Flex>
              </Table.RowCell>
            </Table.Row>
          )
        }}
      />
    </Box>
  )
}
